import React, { useEffect, useMemo, useState } from 'react'

import { Icon } from '@/components/Icon/Icon'
import { CustomImage } from '@/shared/components/CustomImage/CustomImage'
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus'
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import { Box, Chip, styled, Typography } from '@mui/material'

import type { PremadeStoryLook, PremadeStoryRole } from '@/shared/api/models'

type Props = {
  role: PremadeStoryRole
  looks: readonly PremadeStoryLook[]
  selectedLookId: number | null
  onSelect: (lookId: number) => void
  onCreateCustom?: () => void
  showHeading?: boolean
}

type FilterKey = 'gender' | 'hair_color' | 'skin_tone'

type FilterState = Record<FilterKey, string | null>

const EMPTY_FILTERS: FilterState = {
  gender: null,
  hair_color: null,
  skin_tone: null,
}

const GENDER_LABELS: Record<string, string> = {
  male: 'Boy',
  female: 'Girl',
  neutral: 'Neutral',
}

const HAIR_COLOR_SWATCHES: Record<string, string> = {
  blonde: '#e8c47a',
  auburn: '#a0522d',
  brown: '#7b4a25',
  'dark brown': '#3f2410',
  black: '#1a1a1a',
}

const titleCase = (value: string) => value.replace(/(^|\s)\S/g, (m) => m.toUpperCase())

const StyledGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
  gap: theme.spacing(1.5),
  width: '100%',
}))

const StyledCard = styled('button', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>(({ theme, $selected }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 2,
  border: `2px solid ${$selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: $selected ? theme.palette.primary.light + '14' : theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, transform 0.15s ease',
  outline: 'none',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}40`,
  },
}))

const StyledImage = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  backgroundColor: theme.palette.action.hover,
}))

const StyledCheckBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 6,
  right: 6,
  width: 22,
  height: 22,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: theme.shadows[2],
  fontSize: 12,
}))

const StyledCreateCustomCard = styled('button')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 2,
  border: `2px dashed ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  outline: 'none',
  color: theme.palette.text.secondary,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '0A',
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}40`,
  },
}))

const StyledCreateCustomIconWrap = styled(Box)(({ theme }) => ({
  width: '100%',
  aspectRatio: '1 / 1',
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
}))

const StyledEmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}))

const StyledFiltersWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}))

const StyledFilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}))

const StyledFilterLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  minWidth: 56,
}))

const StyledSwatch = styled('span', {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color: string }>(({ $color, theme }) => ({
  display: 'inline-block',
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: $color,
  border: `1px solid ${theme.palette.divider}`,
  marginRight: theme.spacing(0.75),
}))

export const PremadeCharacterPicker: React.FC<Props> = ({
  role, looks, selectedLookId, onSelect, onCreateCustom, showHeading = false,
}) => {
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)

  // Reset filters whenever the role changes (e.g. user navigates between
  // protagonist and sibling roles in a multi-character story).
  useEffect(() => {
    setFilters(EMPTY_FILTERS)
  }, [role.role_key])

  // Filter to looks for this role and apply role-level age/gender constraints
  // when present. Server already filters server-side, but the storefront
  // detail endpoint hands every look in `story.looks`, so this client-side
  // filter keeps the picker focused.
  const roleFilteredLooks = useMemo(() => {
    const allowedAgeGroups = (role.allowed_age_groups as string[] | undefined) ?? []
    const allowedGenders = (role.allowed_genders as string[] | undefined) ?? []

    const matchesRole = looks.filter((look) => look.role_key === role.role_key)
    const ageFiltered = allowedAgeGroups.length
      ? matchesRole.filter((l) => allowedAgeGroups.includes(l.premade_character.age_group as string))
      : matchesRole
    const genderFiltered = allowedGenders.length
      ? ageFiltered.filter((l) => allowedGenders.includes(l.premade_character.gender as string)
          || l.premade_character.gender === 'neutral')
      : ageFiltered

    return [...genderFiltered].sort((a, b) => a.display_order - b.display_order)
  }, [looks, role.role_key, role.allowed_age_groups, role.allowed_genders])

  // Available chip values are derived from the role-filtered set, so the chip
  // row stays stable as the user toggles filters.
  const availableFilterValues = useMemo(() => {
    const collect = (getter: (l: PremadeStoryLook) => string | undefined | null) => {
      const set = new Set<string>()

      for (const look of roleFilteredLooks) {
        const value = getter(look)

        if (value) set.add(value)
      }

      return Array.from(set)
    }

    return {
      gender: collect((l) => l.premade_character.gender as string),
      hair_color: collect((l) => l.premade_character.hair_color),
      skin_tone: collect((l) => l.premade_character.skin_tone),
    }
  }, [roleFilteredLooks])

  const filteredLooks = useMemo(() => roleFilteredLooks.filter((look) => {
    const c = look.premade_character

    if (filters.gender && c.gender !== filters.gender) return false
    if (filters.hair_color && c.hair_color !== filters.hair_color) return false
    if (filters.skin_tone && c.skin_tone !== filters.skin_tone) return false

    return true
  }), [roleFilteredLooks, filters])

  const toggleFilter = (key: FilterKey, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: prev[key] === value ? null : value }))
  }

  const hasActiveFilters = Object.values(filters).some((v) => v !== null)

  const renderFilterRow = (
    key: FilterKey,
    label: string,
    values: string[],
    swatches?: Record<string, string>,
  ) => {
    if (values.length <= 1) return null

    return (
      <StyledFilterRow>
        <StyledFilterLabel>{label}</StyledFilterLabel>
        {values.map((value) => {
          const selected = filters[key] === value
          const displayLabel = key === 'gender'
            ? (GENDER_LABELS[value] ?? titleCase(value))
            : titleCase(value)
          const swatch = swatches?.[value]

          return (
            <Chip
              key={value}
              label={(
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  {swatch && <StyledSwatch $color={swatch} />}
                  {displayLabel}
                </Box>
              )}
              onClick={() => toggleFilter(key, value)}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              size="small"
            />
          )
        })}
      </StyledFilterRow>
    )
  }

  const showFilters = roleFilteredLooks.length > 1
    && (availableFilterValues.gender.length > 1
      || availableFilterValues.hair_color.length > 1
      || availableFilterValues.skin_tone.length > 1)

  return (
    <Box>
      {showHeading && (
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          {role.display_name}
        </Typography>
      )}

      {showFilters && (
        <StyledFiltersWrapper>
          {renderFilterRow('gender', 'Gender', availableFilterValues.gender)}
          {renderFilterRow('hair_color', 'Hair', availableFilterValues.hair_color, HAIR_COLOR_SWATCHES)}
          {hasActiveFilters && (
            <Box>
              <Chip
                label="Clear filters"
                onClick={() => setFilters(EMPTY_FILTERS)}
                size="small"
                variant="outlined"
              />
            </Box>
          )}
        </StyledFiltersWrapper>
      )}

      {filteredLooks.length === 0 ? (
        <StyledEmptyState>
          <Typography variant="body2">
            {hasActiveFilters
              ? 'No characters match these filters. Try clearing some filters.'
              : 'No characters available for this role yet. Try uploading a photo instead.'}
          </Typography>
        </StyledEmptyState>
      ) : (
        <StyledGrid role="radiogroup" aria-label={`Pick a ${role.display_name}`}>
          {filteredLooks.map((look) => {
            const isSelected = look.id === selectedLookId
            const label = look.premade_character.label

            return (
              <StyledCard
                key={look.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={label}
                $selected={isSelected}
                onClick={() => onSelect(look.id)}
              >
                <StyledImage>
                  {look.sticker_image && (
                    <CustomImage
                      src={look.sticker_image}
                      alt={label}
                      variant="square"
                      objectFit="cover"
                      objectPosition="top"
                      sizes="120px"
                      noRatio
                      unoptimized
                    />
                  )}
                  {isSelected && (
                    <StyledCheckBadge>
                      <Icon icon={faCheck} />
                    </StyledCheckBadge>
                  )}
                </StyledImage>
                <Typography variant="caption" sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                  {label}
                </Typography>
              </StyledCard>
            )
          })}
          {onCreateCustom && (
            <StyledCreateCustomCard
              type="button"
              aria-label="Or create your own unique character"
              onClick={onCreateCustom}
            >
              <StyledCreateCustomIconWrap>
                <Icon icon={faPlus} />
              </StyledCreateCustomIconWrap>
              <Typography variant="caption" sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                Or create your own unique character
              </Typography>
            </StyledCreateCustomCard>
          )}
        </StyledGrid>
      )}
    </Box>
  )
}
